import { get, post } from '../utils/networking';
import type {
  HmsRiskLevel,
  ReminderDelivery,
  WorkEnvironmentSurveyStatus,
} from './workEnvironmentTypes';

export type Coverage = 'ok' | 'inProgress' | 'dueSoon' | 'overdue' | 'never';

export type RoutineLink = 'whistleblowing' | 'followUp';

export type FollowUpItem =
  /** The links to the routines are optional, but the law requires the routines */
  | { kind: 'missingRoutines'; links: RoutineLink[] }
  | {
      kind: 'planOverdue';
      teamId: number;
      team: string;
      surveyId: number;
      areas: number;
      deadline: string;
    }
  | {
      kind: 'noSafetyRep';
      teamId: number;
      team: string;
      /** The team leader represents the team, and cannot act for it. Missing from older backends. */
      leaderIsSafetyRep?: boolean;
    }
  | { kind: 'overdue'; teams: number }
  | {
      kind: 'awaitingSignature';
      teamId: number;
      team: string;
      surveyId: number;
      days: number;
    }
  | {
      /** The safety representative returned the documentation, and the team leader went on without the signature */
      kind: 'signatureDeviation';
      teamId: number;
      team: string;
      surveyId: number;
      at: string;
      reason: string | null;
    }
  | {
      kind: 'assessmentDeviation';
      teamId: number;
      team: string;
      surveyId: number;
      at: string;
      reason: string | null;
      /** Without one, the team leader had no safety representative to go through it with */
      hasSafetyRep: boolean;
    }
  | {
      kind: 'approvalDeviation';
      teamId: number;
      team: string;
      surveyId: number;
      planId: number;
      at: string;
      reason: string;
    }
  | { kind: 'never'; teams: number };

export type WatchItem =
  | {
      kind: 'decisionSoon';
      teamId: number;
      team: string;
      surveyId: number;
      areas: number;
      deadline: string;
    }
  | { kind: 'tooFew'; teamId: number; team: string; size: number }
  /** The main safety representative covers the team, which has none of its own */
  | { kind: 'mainSafetyRepOnly'; teamId: number; team: string }
  | {
      kind: 'lowResponses';
      teamId: number;
      team: string;
      surveyId: number;
      responses: number;
      expected: number;
      deadline: string | null;
    }
  | { kind: 'dueSoon'; teams: number };

export type NextDeadline =
  | { kind: 'answer'; date: string | null }
  | { kind: 'plan'; date: string }
  | { kind: 'signature' }
  | { kind: 'review'; date: string }
  | { kind: 'nextSurvey'; date: string };

export type HrOverviewTeam = {
  id: number;
  name: string;
  size: number;
  leaders: string[];
  safetyReps: string[];
  coverage: Coverage;
  survey: {
    id: number;
    status: WorkEnvironmentSurveyStatus;
    createdAt: string;
  } | null;
  /** Unknown until the team has assessed its risk within HR's interval */
  highRisk: number | null;
  next: NextDeadline | null;
};

export type RiskAcrossTeams = {
  key: string;
  name: string;
  sensitive: boolean;
} & Record<HmsRiskLevel, number>;

export type HrWorkEnvironmentOverview = {
  organizationName: string;
  setup: {
    riskMatrix: {
      version: number;
      approvedAt: string | null;
      bySafetyRep: boolean;
    } | null;
    template: { version: number; factors: number } | null;
    intervalMonths: number;
    minResponses: number;
    /** HR can turn safety representatives off where the law does not require them */
    usesSafetyReps: boolean;
    /** Who last turned safety representatives on or off, and when */
    safetyRepsChanged: { by: string | null; at: string } | null;
    /** From the profile of the business, unknown until the admin has filled it in */
    employeeCount: number | null;
  };
  followUp: { now: FollowUpItem[]; later: WatchItem[] };
  risks: RiskAcrossTeams[];
  teamsWithResults: number;
  teams: HrOverviewTeam[];
};

const PATH = '/api/hr/work-environment-overview';

export const getHrWorkEnvironmentOverview = (language: string) =>
  get<HrWorkEnvironmentOverview>(
    `${PATH}?language=${encodeURIComponent(language)}`,
  );

export const remindAboutPlan = (surveyId: number) =>
  post<ReminderDelivery>(`${PATH}/surveys/${surveyId}/plan-reminders`, {});

export const remindToSurvey = (teamIds: number[]) =>
  post<ReminderDelivery>(`${PATH}/survey-reminders`, { teamIds });
